import _ from "lodash";
/**--external--*/
import React from "react";
import classNames from "classnames";
import { connect } from "react-redux";
import { compose } from "lib/apolloClient";
import { ArrowOutOutlined, CloseCircleOutlined } from "@toddle-edu/ds-icons";
import {
  Avatar,
  Badge,
  Checkbox,
  IconButton,
  Tooltip,
} from "@toddle-edu/ds-web";
/**--internal--*/
import MediaPlaceholder from "UIComponents/MediaPlaceholder";
import I18nHOC from "UIComponents/I18nHOC";
import {
  getAttachmentDescription,
  getEvidenceIcon,
} from "SnP/modules/SnPUtils";
import { getDefaultUnitPlanImage } from "UnitPlans/modules/Utils";
import { getRethumbUrl, onEnterOrSpaceKeyDown } from "Utils";
import { getCurrentAssessmentTypes } from "Platform/modules/PlatformModule";
import ConditionalWrapper from "Utils/ConditionalWrapper";
import { getFormattedUserName } from "Utils/GetFormattedUserName";
/**--relative--*/
import classes from "./EvidenceCard.scss";

const BADGE_OFFSET = [2, 4];

const ViewOverlay = ({ openMedia, showDeleteOption, onClickDelete, t }) => {
  const handleStopPropagation = e => {
    e.stopPropagation();
  };

  return (
    <div className={classes.overlay}>
      <IconButton
        icon={<ArrowOutOutlined variant="subtle" />}
        dsVersion="2.0"
        size="small"
        variant="neutral"
        type="outlined"
        className={classes.overlayButton}
        onClick={openMedia}
        aria-label={t("common:open_label_in_new_tab", {
          label: t("common:evidence"),
        })}
        onKeyDown={handleStopPropagation}
      />
      {showDeleteOption ? (
        <IconButton
          icon={<CloseCircleOutlined variant="subtle" />}
          dsVersion="2.0"
          size="small"
          variant="neutral"
          type="outlined"
          className={classes.overlayButton}
          onClick={onClickDelete}
          aria-label={t("common:delete_label", {
            label: t("common:evidence"),
          })}
          onKeyDown={handleStopPropagation}
        />
      ) : null}
    </div>
  );
};

const getRethumb = ({ cardDimensions }) => {
  return imageUrl => {
    const rethumpUrl = imageUrl
      ? getRethumbUrl({
          width: cardDimensions.width,
          height: cardDimensions.height,
          imageUrl: imageUrl,
          fitIn: false,
        })
      : "";
    return `url(${rethumpUrl})`;
  };
};

const EvidenceCard = props => {
  const {
    id,
    createdBy,
    attachments,
    isSelected,
    items,
    type,
    onClickSelection,
    onClickMedia,
    isDisabled,
    isNew,
    data,
    emptySubTitle,
    t,
    showDeleteOption = false,
    onClickDelete,
    testId,
  } = props;

  const handleMediaClick = e => {
    e?.stopPropagation();
    if (onClickMedia) {
      onClickMedia({ data, evidenceType: data.itemType });
    }
  };

  const handleDelete = e => {
    e?.stopPropagation();
    if (onClickDelete) onClickDelete({ data, isSelected });
  };

  const handleSelectClick = () => {
    if (onClickSelection) {
      switch (type) {
        case "UNIT_EVIDENCE":
        case "ORGANIZATION_RESOURCES":
          onClickSelection({ id });
          break;
        case "UNIT_PLANS":
          onClickSelection(data);
          break;
        default:
          onClickSelection();
      }
    }
  };

  const handleSelectKeyDown = e => {
    e.stopPropagation();
    onEnterOrSpaceKeyDown(e, handleSelectClick);
  };

  const containerClassList = classNames(
    {
      [classes.container]: true,
    },
    { [classes.containerDisable]: isDisabled },
    { [classes.containerSelected]: isSelected }
  );
  const getTitle = () => {
    const headingClasses = classNames("text-label", classes.heading);
    let title = "";

    switch (type) {
      case "UNIT_EVIDENCE":
      case "UNIT_PLANS": {
        title = data.title;
        break;
      }
      case "ORGANIZATION_RESOURCES": {
        title = data.label;
        break;
      }
      case "ATTACHMENT":
        title = _.get(data, "attachmentName", "");
        break;
    }

    return (
      <Tooltip dsVersion="2.0" tooltip={title}>
        <div dir="auto" className={headingClasses}>
          {title}
        </div>
      </Tooltip>
    );
  };

  const getDescription = () => {
    const descriptionClasses = classNames("text-body-s", classes.description);
    switch (type) {
      case "UNIT_EVIDENCE": {
        const { id: createdById, profileImage } = createdBy;

        const creatorName = getFormattedUserName({ user: createdBy });

        return (
          <div
            className={classNames(
              descriptionClasses,
              classes.unitEvidenceDescription
            )}
          >
            <Avatar
              dsVersion="2.0"
              shape={"square"}
              id={createdById}
              name={creatorName}
              useSingleInitial
              src={profileImage}
              size="xxx-small"
            />
            <Tooltip dsVersion="2.0" tooltip={creatorName}>
              <div dir="auto" className={classes.createdByText}>
                {creatorName}
              </div>
            </Tooltip>
          </div>
        );
      }
      case "UNIT_PLANS": {
        const displaySubTitle = data.subTitle ? data.subTitle : emptySubTitle;
        const gradeNames = _.get(data, "gradeNames", "");
        const shouldClubGrades = _.size(gradeNames) > 1;

        const grades = (
          <>
            {gradeNames[0]}
            {shouldClubGrades ? (
              <span className={classes.clubbedGrades}>
                ,
                {t("common:plus_more_with_count", {
                  count: _.size(gradeNames) - 1,
                })}
              </span>
            ) : null}
          </>
        );

        return (
          <Tooltip
            dsVersion="2.0"
            tooltip={
              displaySubTitle
                ? `${_.get(data, "grade", "")} • ${displaySubTitle}`
                : _.get(data, "grade", "")
            }
          >
            <div dir="auto" className={descriptionClasses}>
              {displaySubTitle ? (
                <>
                  {grades} • {displaySubTitle}
                </>
              ) : (
                grades
              )}
            </div>
          </Tooltip>
        );
      }
      case "ORGANIZATION_RESOURCES":
      case "ATTACHMENT": {
        const attachmentDescription = getAttachmentDescription(
          _.pick(attachments[0], ["type", "mimeType", "metadata"])
        );

        return (
          <Tooltip dsVersion="2.0" tooltip={attachmentDescription}>
            <div dir="auto" className={descriptionClasses}>
              {attachmentDescription}
            </div>
          </Tooltip>
        );
      }
    }
  };

  const getMediaElement = () => {
    switch (type) {
      case "UNIT_EVIDENCE":
      case "ORGANIZATION_RESOURCES":
      case "ATTACHMENT":
        return (
          <MediaPlaceholder
            attachments={attachments}
            imageMaxHeight={124}
            imageMaxWidth={244}
            resolutionMutliplier={2}
            postEntityItems={items}
            containerStyle={{ height: "100%" }}
          />
        );
      case "UNIT_PLANS": {
        const displayImage = _.get(data, "image", "");
        return (
          <div
            className={classes.imageContainer}
            style={{
              backgroundImage: getDefaultUnitPlanImage({
                id,
                imageUrl: displayImage,
                getRethumb: getRethumb({
                  cardDimensions: {
                    height: 124,
                    width: 244,
                  },
                }),
              }),
            }}
          />
        );
      }
    }
  };

  return (
    <ConditionalWrapper
      type="wrapperAsElement"
      condition={isNew}
      wrapper={
        <Badge
          type="dot"
          dsVersion="2.0"
          offset={BADGE_OFFSET}
          size="xxx-small"
        />
      }
    >
      <div
        onKeyDown={handleSelectKeyDown}
        className={containerClassList}
        onClick={handleSelectClick}
        data-test-id={testId}
      >
        <div className={classes.thumbnail}>
          {getMediaElement()}
          <ViewOverlay
            openMedia={handleMediaClick}
            showDeleteOption={showDeleteOption}
            onClickDelete={handleDelete}
            t={t}
          />
        </div>
        <div className={classes.footer}>
          {type === "ORGANIZATION_RESOURCES" || type === "ATTACHMENT" ? (
            <div className={classes.icon}>
              {getEvidenceIcon({ type: "ATTACHMENT", data: attachments[0] })}
            </div>
          ) : null}
          <div className={classes.content}>
            <div className={classes.labels}>
              {getTitle()}
              {getDescription()}
            </div>
            <div className={classes.actions}>
              <Checkbox isChecked={isSelected} />
            </div>
          </div>
        </div>
      </div>
    </ConditionalWrapper>
  );
};

const mapActionCreators = { getCurrentAssessmentTypes };

export default compose(
  I18nHOC({ resource: ["common", "planningInsights"] }),
  connect(null, mapActionCreators)
)(EvidenceCard);

EvidenceCard.defaultProps = {
  mode: "select",
  customThumbnailElement: null,
  showDescriptionToolTip: false,
  showTitleTooltip: false,
};
